import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import clsx from 'clsx';

export interface Staff {
  id: string; name: string; active: boolean; performsServices?: boolean; color?: string | null; photo?: string | null; chairRental?: boolean;
  services?: { productId: string; price: string | null; durationMin: number | null }[];
}

export function StaffAvatar({ s, size = 40 }: { s: Pick<Staff, 'name' | 'color' | 'photo'>; size?: number }) {
  return s.photo
    ? <img src={s.photo} alt="" className="shrink-0 rounded-full object-cover" style={{ width: size, height: size }} />
    : <span aria-hidden className="grid shrink-0 place-items-center rounded-full font-bold text-white" style={{ width: size, height: size, background: s.color ?? '#64748b', fontSize: size * 0.42 }}>{s.name.slice(0, 1).toUpperCase()}</span>;
}

/** Preço e duração do serviço com este profissional (o próprio, se tiver; senão o do catálogo). */
export function staffPrice(s: Staff | undefined, productId: string): { price: string | null; durationMin: number | null } | null {
  const x = s?.services?.find((v) => v.productId === productId);
  return x ? { price: x.price, durationMin: x.durationMin } : null;
}

/** Quem fez o serviço: grelha de profissionais com fotografia ou cor (F6 na venda). */
export function StaffPicker(p: { staff: Staff[]; current: string | null; title: string; onPick: (id: string | null) => void; onClose: () => void; productId?: string }) {
  const dlg = useRef<HTMLDialogElement>(null);
  useEffect(() => { dlg.current?.showModal(); }, []);
  // Para uma linha: só quem faz esse serviço (ou quem faz todos).
  const list = p.staff.filter((s) => !p.productId || !s.services?.length || s.services.some((v) => v.productId === p.productId));
  return (
    <dialog ref={dlg} onCancel={(e) => { e.preventDefault(); p.onClose(); }} className="m-auto w-[min(640px,96vw)] rounded-2xl bg-[var(--surface)] p-0 text-[var(--text)]">
      <div className="flex items-center justify-between border-b border-base px-5 py-3">
        <h2 className="text-xl font-bold">{p.title}</h2>
        <button aria-label="Fechar" onClick={p.onClose} className="grid h-12 w-12 place-items-center rounded-lg"><X /></button>
      </div>
      <div className="grid grid-cols-2 gap-2 p-5 sm:grid-cols-3">
        {list.map((s) => (
          <button key={s.id} onClick={() => p.onPick(s.id)} aria-label={s.name} aria-pressed={p.current === s.id}
            className={clsx('flex min-h-24 flex-col items-center justify-center gap-2 rounded-2xl border-2 p-3 text-center', p.current === s.id ? 'border-brand-700 bg-brand-50/60 dark:bg-white/5' : 'border-base hover:border-brand-500/50')}>
            <StaffAvatar s={s} size={44} />
            <span className="line-clamp-1 font-semibold">{s.name}</span>
          </button>
        ))}
        <button onClick={() => p.onPick(null)} className="flex min-h-24 items-center justify-center rounded-2xl border-2 border-dashed border-base p-3 text-sm text-muted">Sem profissional</button>
      </div>
    </dialog>
  );
}
